import React from 'react';
import { Heart, Users, Award, Globe } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">About Nanaki Kaur</h1>
          <p className="text-xl opacity-90">
            Empowering women through education, training, employment, and self-sustaining entrepreneurship
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Mission</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At Nanaki Kaur, we join hands with homegrown brands in diverse businesses led by women
              to promote and encourage them in the pursuit of success.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Philosophy</h2>
              <p className="text-gray-700 text-lg mb-6">
                Inspired by the ethereal beauty of the skies and grounded in the resilience of the Earth,
                our specially curated creations are designed to depict surreal beauty and commemorate
                true community values of Sikhism.
              </p>
              <p className="text-gray-700 text-lg italic">
                "Conquering all fields, a pen is all she Wields."
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1636986905406-758b0e280f49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGVtcG93ZXJtZW50fGVufDB8fHx8MTc3NTQyMTY1OXww&ixlib=rb-4.1.0&q=85"
                alt="Women Empowerment"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Concept */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">The Concept</h2>
          <div className="prose prose-lg max-w-4xl mx-auto">
            <p className="text-gray-700 text-lg mb-6">
              At <strong>Nanaki Kaur</strong>, we join hands with <strong>Drishty Foundation</strong> to
              offer Vocational Education and Training in various industries to help them sell under the
              umbrella of Nanaki Kaur (online & offline).
            </p>
            <p className="text-gray-700 text-lg mb-6">
              We then <strong>guarantee employment</strong> of the students who graduate the programme,
              primarily females. After graduation, they go on to work at Nanaki Kaur's Production,
              Sales, or Purchase global departments within the Nanaki Kaur ecosystem.
            </p>
            <p className="text-gray-700 text-lg mb-6">
              We are creating an <strong>ecosystem and community</strong> in the B2B and B2C products & services.
              Using the affiliate marketing programme (Nanaki's Kaurs), we offer a <strong>5% commission</strong> in
              the Sales Division on any products sold offered by Nanaki Kaur brands.
            </p>
            <p className="text-gray-700 text-lg">
              Therefore creating and building an ecosystem and fair trade for the incremental students added
              to our community, who are now <strong>educated, employed & empowered</strong> by Nanaki Kaur
              x Drishty Foundation.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Empowerment</h3>
              <p className="text-gray-600">
                Supporting women through education, training, and entrepreneurship opportunities
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Community</h3>
              <p className="text-gray-600">
                Building a supportive ecosystem where women can thrive together
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Excellence</h3>
              <p className="text-gray-600">
                Delivering quality products crafted with traditional artistry and modern design
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Sustainability</h3>
              <p className="text-gray-600">
                Creating fair trade practices and sustainable employment opportunities
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CSR */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1589104760192-ccab0ce0d90f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3NzU0MjE2ODF8MA&ixlib=rb-4.1.0&q=85"
                alt="Women Education"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">CSR & ESG</h2>
              <p className="text-gray-700 text-lg mb-6">
                Our Corporate Social Responsibility program works with the vision to provide
                <strong> education, training, employment & eventual self-sustaining empowerment</strong> to
                the <strong>female community</strong> through Drishty Foundation.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>100% female-oriented design, manufacturing & affiliated marketing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>5% commission for Nanaki's Kaurs on every sale</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>2.3% royalty on art sales goes directly to artists</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>Monthly donations to Drishty Foundation for social welfare</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Legacy */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Our Legacy</h2>
          <p className="text-xl mb-8 opacity-90">
            Taking one step at a time, for the women running the house, to one day running the world!
          </p>
          <p className="text-lg italic opacity-90">
            A way to give back to the female-community by singh(s)
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;